'use client';

import { useCallback, useEffect, useMemo, useState } from 'react';
import type { AuthChangeEvent, Session } from '@supabase/supabase-js';
import { createBrowserSupabaseClient } from '../../utils/supabase-client.ts';
import type { AuthProviderState, AuthActions } from './types.ts';

function createInitialState(initialSession?: Session | null): AuthProviderState {
  return {
    session: initialSession ?? null,
    isLoading: initialSession === undefined,
    error: null,
    isAnonymous: initialSession?.user?.is_anonymous ?? false,
  };
}

/**
 * Internal hook that manages auth state and provides actions.
 * Only used by AuthProvider - consumers should use useAuthState/useAuthActions.
 */
export function useAuthProvider(initialSession?: Session | null): {
  state: AuthProviderState;
  actions: AuthActions;
} {
  const [state, setState] = useState<AuthProviderState>(() =>
    createInitialState(initialSession)
  );

  const supabase = createBrowserSupabaseClient();

  // Initialize session on mount only if no initial session was provided
  useEffect(() => {
    if (initialSession !== undefined) {
      return;
    }

    let isMounted = true;

    const initializeAuth = async () => {
      try {
        const { data } = await supabase.auth.getSession();
        if (isMounted) {
          setState((prev) => ({
            ...prev,
            session: data.session,
            isLoading: false,
            isAnonymous: data.session?.user?.is_anonymous ?? false,
          }));
        }
      } catch (error) {
        if (isMounted) {
          setState((prev) => ({
            ...prev,
            error:
              error instanceof Error
                ? error
                : new Error('Failed to get session'),
            isLoading: false,
          }));
        }
      }
    };

    initializeAuth();

    return () => {
      isMounted = false;
    };
  }, [supabase, initialSession]);

  // Listen for auth state changes
  useEffect(() => {
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(
      (event: AuthChangeEvent, newSession: Session | null) => {
        setState((prev) => ({
          ...prev,
          session: newSession,
          error: null,
          isAnonymous: newSession?.user?.is_anonymous ?? false,
        }));

        // Broadcast to other tabs
        if (typeof window !== 'undefined') {
          window.localStorage.setItem(
            'auth-event',
            JSON.stringify({ timestamp: Date.now() })
          );

          // Only redirect on explicit logout event
          if (event === 'SIGNED_OUT') {
            window.location.href = '/login';
          }
        }
      }
    );

    return () => {
      subscription?.unsubscribe();
    };
  }, [supabase]);

  // Listen for auth changes from other tabs
  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'auth-event' && e.newValue) {
        supabase.auth
          .getSession()
          .then(({ data }: { data: { session: Session | null } }) => {
            setState((prev) => ({
              ...prev,
              session: data.session,
              isAnonymous: data.session?.user?.is_anonymous ?? false,
            }));
          });
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, [supabase]);

  // -------------------------------------------------------------------------
  // Auth Actions
  // -------------------------------------------------------------------------

  const signUp = useCallback(
    async (email: string, password: string) => {
      try {
        const { data, error } = await supabase.auth.signUp({ email, password });
        if (error) {
          setState((prev) => ({ ...prev, error }));
          return { error, session: null };
        }
        if (data.session) {
          setState((prev) => ({
            ...prev,
            session: data.session,
            error: null,
          }));
        }
        return { error: null, session: data.session };
      } catch (error) {
        const err =
          error instanceof Error ? error : new Error('Sign up failed');
        setState((prev) => ({ ...prev, error: err }));
        return { error: err, session: null };
      }
    },
    [supabase]
  );

  const signIn = useCallback(
    async (email: string, password: string) => {
      try {
        const { error } = await supabase.auth.signInWithPassword({
          email,
          password,
        });
        if (error) {
          setState((prev) => ({ ...prev, error }));
          return { error };
        }

        const { data } = await supabase.auth.getSession();
        setState((prev) => ({
          ...prev,
          session: data.session,
          error: null,
        }));
        return { error: null };
      } catch (error) {
        const err =
          error instanceof Error ? error : new Error('Sign in failed');
        setState((prev) => ({ ...prev, error: err }));
        return { error: err };
      }
    },
    [supabase]
  );

  const sendMagicLink = useCallback(
    async (email: string, redirectTo?: string) => {
      try {
        const emailRedirectTo =
          redirectTo ??
          `${window.location.origin}/auth/callback?next=/sec`;
        const { error } = await supabase.auth.signInWithOtp({
          email,
          options: {
            emailRedirectTo,
            shouldCreateUser: false,
          },
        });

        if (error) {
          setState((prev) => ({ ...prev, error }));
          return { error };
        }

        setState((prev) => ({
          ...prev,
          error: null,
        }));
        return { error: null };
      } catch (error) {
        const err =
          error instanceof Error
            ? error
            : new Error('Magic link sign in failed');
        setState((prev) => ({ ...prev, error: err }));
        return { error: err };
      }
    },
    [supabase]
  );

  const signInAnonymously = useCallback(async () => {
    // Don't overwrite existing session (real or anonymous)
    const { data: existingData } = await supabase.auth.getSession();
    if (existingData.session) {
      return { error: null, session: existingData.session };
    }

    try {
      const { data, error } = await supabase.auth.signInAnonymously();
      if (error) {
        setState((prev) => ({ ...prev, error }));
        return { error, session: null };
      }
      if (data.session) {
        setState((prev) => ({
          ...prev,
          session: data.session,
          isAnonymous: true,
          error: null,
        }));
      }
      return { error: null, session: data.session };
    } catch (error) {
      const err =
        error instanceof Error ? error : new Error('Anonymous sign in failed');
      setState((prev) => ({ ...prev, error: err }));
      return { error: err, session: null };
    }
  }, [supabase]);

  const signOut = useCallback(async () => {
    try {
      await supabase.auth.signOut();
    } catch (error) {
      setState((prev) => ({
        ...prev,
        error: error instanceof Error ? error : new Error('Sign out failed'),
      }));
    }
  }, [supabase]);

  const resetPassword = useCallback(
    async (email: string) => {
      try {
        const { error } = await supabase.auth.resetPasswordForEmail(email, {
          redirectTo: `${window.location.origin}/auth/callback?next=/reset`,
        });
        if (error) {
          setState((prev) => ({ ...prev, error }));
          return { error };
        }
        return { error: null };
      } catch (error) {
        const err =
          error instanceof Error ? error : new Error('Password reset failed');
        setState((prev) => ({ ...prev, error: err }));
        return { error: err };
      }
    },
    [supabase]
  );

  const updatePassword = useCallback(
    async (newPassword: string) => {
      try {
        const { error } = await supabase.auth.updateUser({
          password: newPassword,
        });
        if (error) {
          setState((prev) => ({ ...prev, error }));
          return { error };
        }
        return { error: null };
      } catch (error) {
        const err =
          error instanceof Error ? error : new Error('Password update failed');
        setState((prev) => ({ ...prev, error: err }));
        return { error: err };
      }
    },
    [supabase]
  );

  const actions = useMemo<AuthActions>(
    () => ({
      signUp,
      signIn,
      sendMagicLink,
      signInAnonymously,
      signOut,
      resetPassword,
      updatePassword,
    }),
    [
      signUp,
      signIn,
      sendMagicLink,
      signInAnonymously,
      signOut,
      resetPassword,
      updatePassword,
    ]
  );

  return { state, actions };
}
